import './navbar.scss';

import PropTypes from 'prop-types';
import { useAuth } from 'react-oidc-context';
import React from 'react';
import ClickAwayListener from 'react-click-away-listener';
import { QueryCache } from 'react-query';
import { useHistory } from 'react-router-dom';

import welcomeAvatar from '../../assets/images/welcome-avatar.png';
import { ReactComponent as Logout } from '../../assets/icons/logout.svg';
import { ReactComponent as Notification } from '../../assets/icons/notification.svg';
// import { ReactComponent as Search } from '../../assets/icons/search.svg';
import { ReactComponent as Settings } from '../../assets/icons/settings.svg';
import { IS_ADMIN } from '../../config';
import { useHasPermission, useLogout } from '../../hooks';
import { Breadcrumb, Button, Tabs } from '..';
import { NextSteps } from '../next-steps';
import { ProfileImage } from '../profile-image/profile-image';
import Logo from '../vectors/Logo';
import { Notifications } from './notifications';
import { PageTitle } from './page-title';
import { SwitchModes } from './switch-modes';
import { useNavBar } from './use-navbar';

const NOTIFICATIONS = 'NOTIFICATIONS';
const PROFILE = 'PROFILE';

const Navbar = ({ openSideBar, menuItems, ...props }) => {
  const {
    openMenu,
    user,
    setOpenMenu,
    isLive,
    currentDropdown,
    disableSwitch,
    pageTitle = { normal: '', cursive: '' },
    tabs,
    tabBaseUrl,
    handleLiveChange,
    toggleDropdown,
    closeDropdown,
    breadcrumb,
    cta
  } = useNavBar({ menuItems, updateMerchant: props?.updateMerchant });

  const { normal = '', cursive = '' } = pageTitle || {};
  // An array replaces the page title inline; `true` keeps the legacy bar below.
  const inlineCrumbs = Array.isArray(breadcrumb) ? breadcrumb : null;

  const CurrentDropdown = dropdownList[currentDropdown] || React.Fragment;
  return (
    <header>
      <div className="navbar">
        <div className="navbar__title">
          {inlineCrumbs ? (
            <Breadcrumb crumbs={inlineCrumbs} variant="breadcrumb--inline" />
          ) : (
            <div className="navbar__title-lead">
              <img src={welcomeAvatar} alt="" aria-hidden="true" className="navbar__title-avatar" />
              <PageTitle normal={normal} cursive={cursive} user={user} />
            </div>
          )}
          <button
            type="button"
            className="navbar__title-bell"
            aria-label="Notifications"
            onClick={() => toggleDropdown(NOTIFICATIONS)}
          >
            <Notification />
          </button>
        </div>
        <hr className="navbar__title-divider" />
        <div className="navbar__menu">
          <SwitchModes
            isLive={isLive}
            handleLiveChange={handleLiveChange}
            disableSwitch={disableSwitch}
          />
          <div className="navbar__menu-item logo">
            <Logo />
          </div>
          <button role="menu" className="navbar__menu-item --mobile-trigger" onClick={openSideBar}>
            <span />
          </button>
          {/* <button className="navbar__menu-item">
            <Search />
          </button> */}
          <button className="navbar__menu-item --image " onClick={() => toggleDropdown(PROFILE)}>
            <ProfileImage location="navbar" />
          </button>
          <button
            className="navbar__menu-item notifications"
            onClick={() => toggleDropdown(NOTIFICATIONS)}
          >
            <Notification />
          </button>

          {currentDropdown && <CurrentDropdown onClose={closeDropdown} />}

          {openMenu && <NavbarDropdown onClose={() => openMenu && setOpenMenu(false)} />}
        </div>
      </div>

      {!IS_ADMIN && <NextSteps hiddenPaths={['/onboard']} />}
      {breadcrumb === true && <Breadcrumb />}
      {(tabs || cta) && (
        <div className="navbar__tabs-row">
          {tabs && <Tabs tabList={tabs} baseUrl={tabBaseUrl} />}
          {cta && <NavbarCta cta={cta} />}
        </div>
      )}
    </header>
  );
};

const NavbarCta = ({ cta }) => {
  const history = useHistory();
  const hasPermission = useHasPermission();
  // Hide the CTA on its own target path — otherwise pages with
  // hasSubRoutes (e.g. /conversions/convert under /conversions) would
  // see a "go where you already are" button.
  if (history.location.pathname.startsWith(cta.target)) return null;
  if (cta.permission && !hasPermission(cta.permission)) return null;
  return (
    <Button className="navbar__cta" onClick={() => history.push(cta.target)}>
      {cta.label}
    </Button>
  );
};

NavbarCta.propTypes = {
  cta: PropTypes.shape({
    label: PropTypes.string.isRequired,
    target: PropTypes.string.isRequired,
    permission: PropTypes.string
  }).isRequired
};

const NavbarDropdown = ({ onClose }) => {
  const history = useHistory();
  const logout = useLogout();
  const openSettings = () => {
    history.push('/settings');
    onClose();
  };
  const clearSession = () => {
    onClose();
    logout();
  };
  return (
    <ClickAwayListener onClickAway={() => onClose()}>
      <div className="navbar__dropdown-menu">
        <button className="navbar__dropdown-menu-item" onClick={openSettings}>
          <Settings />
          Settings
        </button>
        <button className="navbar__dropdown-menu-item text-error" onClick={clearSession}>
          <Logout />
          Logout
        </button>
      </div>
    </ClickAwayListener>
  );
};

const dropdownList = {
  NOTIFICATIONS: Notifications,
  PROFILE: NavbarDropdown
};

Navbar.defaultProps = {
  menuItems: [],
  updateMerchant: () => null
};
Navbar.propTypes = {
  openSideBar: PropTypes.func,
  menuItems: PropTypes.array,
  updateMerchant: PropTypes.func
};

NavbarDropdown.propTypes = {
  onClose: PropTypes.func
};

export { Navbar };
